// src/pages/Settings.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Settings as SettingsIcon, Save, Database, Shield, Bell } from 'lucide-react';

const Settings = () => {
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-100">System Settings</h2>
          <p className="text-slate-400 mt-2">Configure platform settings and preferences</p>
        </div>
        <button className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-medium rounded-lg transition-all duration-200 flex items-center">
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </button>
      </div>

      {/* Settings Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="admin-glass rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center mb-4">
            <SettingsIcon className="w-6 h-6 text-red-400 mr-3" />
            <h3 className="text-xl font-bold text-slate-100">General Settings</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Platform Name</label>
              <input
                type="text"
                defaultValue="SpaceJet Network"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Support Email</label>
              <input
                type="email"
                defaultValue="growth@spacejetmedia.com"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="admin-glass rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center mb-4">
            <Shield className="w-6 h-6 text-red-400 mr-3" />
            <h3 className="text-xl font-bold text-slate-100">Security Settings</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-slate-300">Two-Factor Authentication</label>
                <p className="text-xs text-slate-400">Require 2FA for admin accounts</p>
              </div>
              <input type="checkbox" className="rounded bg-slate-700 border-slate-600" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-slate-300">Session Timeout</label>
                <p className="text-xs text-slate-400">Auto-logout after inactivity</p>
              </div>
              <select className="px-3 py-1 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm">
                <option>30 minutes</option>
                <option>1 hour</option>
                <option>2 hours</option>
              </select>
            </div>
          </div>
        </div>

        {/* Database Settings */}
        <div className="admin-glass rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center mb-4">
            <Database className="w-6 h-6 text-red-400 mr-3" />
            <h3 className="text-xl font-bold text-slate-100">Database Settings</h3>
          </div>
          
          <div className="h-32 flex items-center justify-center border-2 border-dashed border-slate-700 rounded-lg">
            <div className="text-center">
              <Database className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">Database configuration panel</p>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="admin-glass rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center mb-4">
            <Bell className="w-6 h-6 text-red-400 mr-3" />
            <h3 className="text-xl font-bold text-slate-100">Notifications</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-slate-300">Email Notifications</label>
                <p className="text-xs text-slate-400">System alerts and updates</p>
              </div>
              <input type="checkbox" defaultChecked className="rounded bg-slate-700 border-slate-600" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-slate-300">SMS Alerts</label>
                <p className="text-xs text-slate-400">Critical system notifications</p>
              </div>
              <input type="checkbox" className="rounded bg-slate-700 border-slate-600" />
            </div>
          </div>
        </div>

        {/* Import Operations */}
        <div className="admin-glass rounded-xl p-6 border border-slate-700/50 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-slate-100">Automation + Management Workspaces</h3>
              <p className="text-sm text-slate-400 mt-1">Automations handles data syncing/import jobs, Management handles day-to-day campaign operations.</p>
            </div>
            <div className="flex gap-2">
              <Link to="/automations" className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg text-sm">Open Automations</Link>
              <Link to="/management" className="px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg text-sm">Open Management</Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
              <p className="text-sm text-slate-100 font-semibold">Automations Workspace</p>
              <p className="text-xs text-slate-400 mt-2">Run advertiser/campaign/affiliate sync jobs, campaign imports, and mapping profile setup.</p>
              <p className="text-xs text-amber-200 mt-3">Automation mapping profiles are saved and visible per advertiser in Management.</p>
            </div>

            <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
              <p className="text-sm text-slate-100 font-semibold">Management Workspace</p>
              <p className="text-xs text-slate-400 mt-2">Manage imported advertisers/affiliates/campaigns, add creatives to campaigns, and generate links in bulk.</p>
              <p className="text-xs text-slate-500 mt-3">Designed for daily operations and campaign execution.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
